import { Form } from 'antd';
import { useCallback, useState } from 'react';

import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import { useLazyGetPaymentAccountsForSystemQuery } from '@app/api/PaymentAccounts';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NClients } from '@app/types/Clients';
import type { ErrorResponse } from '@app/types/Errors';

const { useForm } = Form;

export const usePaidModal = (
  handleTopClientBalance: (args: NClients.HandleTopClientBalance) => void,
  branchId?: number | null
) => {
  const [form] = useForm();
  const [paymentAccounts, setPaymentAccounts] = useState<ModelPaymentAccount[]>(
    []
  );

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [getPaymentAccountsForSystem] =
    useLazyGetPaymentAccountsForSystemQuery();

  const getPaymentAccountsForSystemHandler = useCallback(async () => {
    // Cashboxes must be fetched for the same branch the top-up request
    // declares (the lead card's branch), otherwise the selected cashbox does
    // not belong to the request branch and the backend rejects it with
    // "Касса с ID N не найдена".
    const targetBranchId = branchId ?? activeBranchId;
    try {
      const result = await getPaymentAccountsForSystem({
        ActiveBranchId: targetBranchId,
        branchId: targetBranchId,
        includeDeleted: false,
      }).unwrap();

      setPaymentAccounts(result);
    } catch (error) {
      const errorMessage = (error as ErrorResponse).data || '';
      showNotification(errorMessage, WARNING);
      throw new Error(errorMessage);
    }
  }, [activeBranchId, branchId, getPaymentAccountsForSystem, showNotification]);

  const handleDialogOpening = useCallback(
    (isOpened: boolean): void => {
      if (!isOpened) return;

      getPaymentAccountsForSystemHandler();
    },
    [getPaymentAccountsForSystemHandler]
  );

  const submitForm = useCallback(
    (values: NClients.HandleTopClientBalance) => {
      const { Amount, Comment, PaymentAccountId } = values;
      handleTopClientBalance({ Amount, Comment, PaymentAccountId, form });
    },
    [form, handleTopClientBalance]
  );

  return {
    form,
    paymentAccounts,
    submitForm,
    handleDialogOpening,
  };
};
